/**
 * Smena halqasi: o'tgan / qolgan vaqt (xodimning o'z zonasida), o'rtacha o'lchamda AQSh zonalarida hozir ish vaqtimi
 * (9:00-17:00 mahalliy) -- qaysi shtatlarga hozir qo'ng'iroq tabiiy ekani bir qarashda.
 */
import { useTranslation } from "react-i18next";
import { Timer } from "lucide-react";
import { cn } from "@/lib/utils";
import { useNow } from "@/lib/useNow";
import type { Me } from "@/features/auth/types";
import type { WidgetItem } from "../types";
import { autoHour12, clockText, inCallHours, US_ZONES, zoneParts, zoneShort } from "../clock/time";
import { Ring } from "../ui";
import type { ShiftConfig } from "./index";
import { hm, shiftState } from "./time";

type Props = { item: WidgetItem<ShiftConfig>; me: Me };
const US_BUSINESS: readonly [number, number] = [9, 17];

function useShift(me: Me, c: ShiftConfig) {
  const now = useNow("minute");
  const tz = me.timezone || "Asia/Tashkent";
  return { now, state: shiftState(now, tz, c.start, c.end) };
}

export function ShiftView({ item, me }: Props) {
  const { t, i18n } = useTranslation("widgets");
  const { now, state } = useShift(me, item.config);
  const hour12 = autoHour12(i18n.resolvedLanguage ?? i18n.language);
  const during = state.phase === "during";
  const big = during ? hm(state.remainingMin) : hm(state.untilStartMin);
  const label = t(during ? "shift.left" : "shift.startsIn");
  const ring = (
    <Ring value={during ? state.fraction : 0} size={88} stroke={8} className={cn(during ? "text-ink" : "text-faint")}>
      <div className="text-[20px] leading-none font-semibold tracking-tight tabular-nums">{big}</div>
      <div className="mt-0.5 max-w-[64px] truncate text-[10px] text-muted">{label}</div>
    </Ring>
  );
  if (item.size === "s") {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-1.5 px-3 py-2">
        {ring}
        <div className="text-[11px] text-muted tabular-nums">
          {item.config.start}–{item.config.end}
          {state.phase === "after" && ` · ${t("shift.ended")}`}
        </div>
      </div>
    );
  }
  return (
    <div className="flex h-full items-center gap-3 px-3 py-2.5">
      {ring}
      <div className="min-w-0 flex-1 border-l border-line pl-3">
        <div className="text-[12px] font-semibold">{t("shift.title")}</div>
        <div className="text-[11px] text-muted tabular-nums">
          {item.config.start}–{item.config.end}
          {during && ` · ${t("shift.elapsed", { time: hm(state.elapsedMin) })}`}
          {state.phase === "after" && ` · ${t("shift.ended")}`}
        </div>
        <div className="mt-2 text-[10px] font-medium tracking-wide text-muted uppercase">{t("shift.usHours")}</div>
        <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
          {US_ZONES.map((tz) => {
            const p = zoneParts(now, tz);
            const open = inCallHours(p, US_BUSINESS);
            return (
              <span key={tz} className={cn("inline-flex items-center gap-1 text-[11px] tabular-nums", open ? "text-ink" : "text-muted")}>
                <span className={cn("h-1.5 w-1.5 rounded-full", open ? "bg-success" : "bg-faint")} aria-hidden />
                {zoneShort(tz)} {clockText(p, hour12).time}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function ShiftGlance({ item, me }: Props) {
  const { t } = useTranslation("widgets");
  const { state } = useShift(me, item.config);
  const during = state.phase === "during";
  return (
    <span className="inline-flex items-center gap-1.5" title={t("shift.title")}>
      <Timer size={13} strokeWidth={1.75} className="text-muted" aria-hidden />
      <span className={cn("text-[12px] font-semibold tabular-nums", !during && "text-muted")}>{during ? hm(state.remainingMin) : hm(state.untilStartMin)}</span>
      <span className="text-[11px] text-muted">{t(during ? "shift.left" : "shift.startsIn")}</span>
    </span>
  );
}
